import { 
    THEME_COLOR, 
    DARK_MODE,
    GRAY_SCALE,
    BORDER,
    BODY_BACKGROUND,
    SHADOWS,
    SAB_SAFFRON,
    SAB_NAVY
} from 'constants/ThemeConstant'

export function rgba(hex, opacity = 1) {
    if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      throw new Error('Invalid Hex');
    }
  
    const decimal = parseInt(hex.substring(1), 16);
  
    const r = (decimal >> 16) & 255;
    const g = (decimal >> 8) & 255;
    const b = decimal & 255;
  
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// SAB Primary Color - Saffron Orange
const primaryColor = SAB_SAFFRON;

export const baseTheme = {
    borderRadius: 12,
    colorPrimary: primaryColor,
    colorSuccess: THEME_COLOR.GREEN,
    colorWarning: THEME_COLOR.GOLD,
    colorError: THEME_COLOR.RED,
    colorInfo: THEME_COLOR.BLUE,
    colorText: GRAY_SCALE.GRAY,
    colorBorder: BORDER.BASE_COLOR,
    bodyBg: BODY_BACKGROUND,
    controlHeight: 42,
    controlHeightLG: 50,
    controlHeightSM: 36,
    fontFamily: `'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
    fontSizeHeading1: 32,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 17,
    fontSizeHeading5: 15,
    lineHeight: 1.6,
    boxShadow: SHADOWS.MD,
    boxShadowSecondary: SHADOWS.LG,
}

const getBaseComponentThemeConfig = (
    {
        Button = {},
        Menu = {}, 
        Card = {},
        Table = {},
        Select = {},
        DatePicker = {},
        Layout = {},
        Input = {},
        Dropdown = {},
        Calendar = {},
        Drawer = {},
        Modal = {},
        Tag = {},
    } = {}
) => {
    return {
        Button: {
            borderRadius: 10,
            controlHeight: 42,
            controlHeightLG: 50,
            controlHeightSM: 36,
            paddingContentHorizontal: 20,
            fontWeight: 500,
            primaryShadow: '0 2px 0 rgba(249, 115, 22, 0.1)',
            defaultShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
            ...Button
        },
        Dropdown: {
            controlPaddingHorizontal: 16,
            controlHeight: 40,
            borderRadiusLG: 12,
            paddingXXS: '6px 0px',
            boxShadowSecondary: SHADOWS.LG,
            ...Dropdown
        },
        Calendar: {
            borderRadius: 12,
            ...Calendar
        },
        Checkbox: {
            lineWidth: 2,
            borderRadiusSM: 6,
            controlInteractiveSize: 18,
        },
        Card: {
            colorTextHeading: GRAY_SCALE.GRAY_DARK,
            paddingLG: 24,
            borderRadiusLG: 16,
            boxShadow: SHADOWS.SM,
            headerFontSize: 16,
            headerFontSizeSM: 14,
            ...Card
        },
        Layout: {
            bodyBg: BODY_BACKGROUND,
            headerBg: GRAY_SCALE.WHITE,
            siderBg: GRAY_SCALE.WHITE,
            ...Layout
        },
        Breadcrumb: {
            colorTextDescription: GRAY_SCALE.GRAY_LIGHT,
            colorText: primaryColor,
            colorBgTextHover: 'transparent',
            fontSize: 14,
        },
        Menu: {
            itemBg: 'transparent',
            colorActiveBarHeight: 0,
            activeBarWidth: 4,
            horizontalItemSelectedColor: primaryColor,
            itemHoverColor: primaryColor,
            itemSelectedColor: primaryColor,
            itemSelectedBg: rgba(primaryColor, 0.08),
            itemHoverBg: rgba(primaryColor, 0.04),
            radiusItem: 10,
            subMenuItemBg: 'transparent',
            itemMarginInline: 8,
            itemMarginBlock: 4,
            controlHeightLG: 44,
            controlHeightSM: 24,
            iconSize: 18,
            collapsedIconSize: 20,
            fontSize: 14,
            groupTitleFontSize: 12,
            ...Menu
        },
        Input: {
            borderRadius: 10,
            controlHeight: 42,
            controlHeightLG: 50,
            controlHeightSM: 36,
            paddingInline: 14,
            ...Input
        },
        Pagination: {
            paginationItemSize: 36,
            borderRadius: 10,
            colorBgContainer: primaryColor,
            colorPrimary: GRAY_SCALE.WHITE,
            colorPrimaryHover: GRAY_SCALE.WHITE,
            itemActiveBg: primaryColor,
        },
        Steps: {
            wireframe: true,
            controlHeight: 36,
            waitIconColor: GRAY_SCALE.GRAY_LIGHT,
            iconSize: 28,
        },
        DatePicker: {
            controlHeight: 42,
            controlHeightSM: 32,
            borderRadiusSM: 8,
            lineWidthBold: 0,
            borderRadius: 10,
            ...DatePicker
        },
        Radio: {
            fontSizeLG: 16,
            dotSize: 10,
            radioSize: 18,
        },
        Slider: {
            colorPrimaryBorder: rgba(primaryColor, 0.8),
            colorPrimaryBorderHover: primaryColor,
            handleSize: 14,
            handleSizeHover: 16,
            railSize: 6,
        },
        Select: {
            paddingXXS: '6px 0px',
            controlHeight: 42,
            controlHeightSM: 36,
            controlItemBgActive: rgba(primaryColor, 0.08),
            controlItemBgHover: rgba(primaryColor, 0.04),
            borderRadius: 10,
            optionSelectedBg: rgba(primaryColor, 0.08),
            ...Select
        },
        TreeSelect: {
            controlHeightSM: 28,
            borderRadius: 10,
        },
        Tooltip: {
            colorBgDefault: rgba(GRAY_SCALE.DARK, 0.9),
            controlHeight: 36,
            borderRadius: 8,
            fontSize: 13,
        },
        Timeline: {
            lineType: 'dashed',
            dotBorderWidth: 3,
        },
        Tag: {
            lineHeight: 2.2,
            borderRadiusSM: 6,
            fontSizeSM: 12,
            defaultBg: rgba(primaryColor, 0.06),
            ...Tag
        },
        Table: {
            colorText: GRAY_SCALE.GRAY,
            tableHeaderBg: GRAY_SCALE.GRAY_LIGHTEST,
            borderRadius: 12,
            headerBorderRadius: 12,
            cellPaddingBlock: 16,
            cellPaddingInline: 16,
            headerSplitColor: 'transparent',
            rowHoverBg: rgba(primaryColor, 0.02),
            ...Table
        },
        Drawer: {
            borderRadius: 0,
            paddingLG: 24,
            ...Drawer
        },
        Modal: {
            borderRadiusLG: 16,
            paddingLG: 24,
            headerBg: GRAY_SCALE.WHITE,
            contentBg: GRAY_SCALE.WHITE,
            ...Modal
        },
        Alert: {
            borderRadiusLG: 12,
            fontSize: 14,
        },
        Message: {
            borderRadiusLG: 12,
        },
        Notification: {
            borderRadiusLG: 12,
        },
        Switch: {
            trackHeight: 24,
            trackMinWidth: 44,
            handleSize: 20,
        },
        Tabs: {
            itemSelectedColor: primaryColor,
            itemHoverColor: primaryColor,
            inkBarColor: primaryColor,
            cardBg: GRAY_SCALE.WHITE,
            titleFontSize: 14,
        },
        Badge: {
            dotSize: 8,
            fontSize: 11,
            fontSizeSM: 11,
        },
        Avatar: {
            borderRadius: 10,
        },
        Progress: {
            circleTextFontSize: '1em',
            defaultColor: primaryColor,
        },
        Statistic: {
            titleFontSize: 13,
            contentFontSize: 28,
        },
        Empty: {
            colorTextDescription: GRAY_SCALE.GRAY_LIGHT,
        },
        Form: {
            labelFontSize: 14,
            verticalLabelPadding: '0 0 8px',
            itemMarginBottom: 20,
        },
        InputNumber: {
            borderRadius: 10,
            controlHeight: 42,
            controlHeightLG: 50,
            controlHeightSM: 36,
        },
        TimePicker: {
            borderRadius: 10,
            controlHeight: 42,
        },
        Segmented: {
            borderRadius: 10,
            itemSelectedBg: GRAY_SCALE.WHITE,
            trackBg: GRAY_SCALE.GRAY_LIGHTEST,
        },
        Collapse: {
            borderRadiusLG: 12,
            headerBg: GRAY_SCALE.GRAY_LIGHTEST,
        },
        Popover: {
            borderRadiusLG: 12,
        },
        Popconfirm: {
            borderRadiusLG: 12,
        },
        ColorPicker: {
            borderRadius: 10,
        },
    }
}

export const lightTheme = {
    token: {
        ...baseTheme,
        colorTextBase: GRAY_SCALE.GRAY,
        colorBgContainer: GRAY_SCALE.WHITE,
        colorBgElevated: GRAY_SCALE.WHITE,
        colorBgLayout: BODY_BACKGROUND,
    },
    components: getBaseComponentThemeConfig()
}

export const darkTheme = {
    token: {
        ...baseTheme,
        colorTextBase: DARK_MODE.TEXT_COLOR,
        colorBgBase: DARK_MODE.BG_COLOR,
        colorBorder: DARK_MODE.BORDER_BASE_COLOR,
        colorText: DARK_MODE.TEXT_COLOR,
        colorBgContainer: DARK_MODE.BG_COLOR,
        colorBgElevated: '#283142',
        colorBgLayout: '#0f172a',
    },
    components: getBaseComponentThemeConfig({
        Button: {
            controlOutline: 'transparent',
            colorBorder: DARK_MODE.BORDER_BASE_COLOR,
            colorText: DARK_MODE.TEXT_COLOR,
            defaultBg: DARK_MODE.BG_COLOR,
        },
        Card: {
            colorTextHeading: DARK_MODE.HEADING_COLOR,
            colorBgContainer: DARK_MODE.BG_COLOR,
        },
        Layout: {
            bodyBg: '#0f172a',
            headerBg: DARK_MODE.BG_COLOR,
            siderBg: DARK_MODE.BG_COLOR,
        },
        Menu: {
            itemSelectedBg: rgba(primaryColor, 0.15),
            horizontalItemSelectedColor: GRAY_SCALE.WHITE,
            itemHoverColor: primaryColor,
            itemSelectedColor: primaryColor,
            colorItemText: DARK_MODE.TEXT_COLOR,
            activeBarWidth: 4,
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
            darkItemBg: DARK_MODE.BG_COLOR,
            darkSubMenuItemBg: '#1a2744',
        },
        Dropdown: {
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
            colorBgElevated: DARK_MODE.BG_COLOR,
        },
        Calendar: {
            controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
        },
        Select: {
            controlOutline: 'transparent',
            controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
            controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
            colorBgElevated: DARK_MODE.BG_COLOR,
        },
        Input: {
            controlOutline: 'transparent',
            colorBgContainer: DARK_MODE.BG_COLOR,
        },
        DatePicker: {
            controlOutline: 'transparent',
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
            controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
            controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
            colorBgElevated: DARK_MODE.BG_COLOR,
        },
        Table: {
            colorText: DARK_MODE.TEXT_COLOR,
            colorTextHeading: DARK_MODE.HEADING_COLOR,
            headerBg: '#1a2744',
            colorBgContainer: DARK_MODE.BG_COLOR,
            rowHoverBg: DARK_MODE.HOVER_BG_COLOR,
        },
        Drawer: {
            colorBgElevated: DARK_MODE.BG_COLOR,
        },
        Modal: {
            headerBg: DARK_MODE.BG_COLOR,
            contentBg: DARK_MODE.BG_COLOR,
        },
        Tag: {
            defaultBg: DARK_MODE.HOVER_BG_COLOR,
        },
    })
}
